import React, { ReactNode, useCallback, useState } from 'react';

import classNames from 'classnames';
import { useLocation } from 'react-router-dom';
import { Fade } from '@mui/material';

import { LocalStorage } from '@vk-hr-tek/core/local-storage';
import { useInject } from '@vk-hr-tek/core/ioc';
import { Box } from '@vk-hr-tek/ui/Box';
import { Tooltip } from '@vk-hr-tek/ui/Tooltip';
import { Icon } from '@vk-hr-tek/ui/Icon';
import { Typography } from '@vk-hr-tek/ui/Typography';
import { Link } from '@vk-hr-tek/ui/Link';

import { NavigationExternalItem } from '@app/router';
import { useDispatch, useSelector } from '@app/hooks';
import { UserRoleEnum } from '@app/types';

import { useUrlWithPrefix } from '../../hooks';
import {
  selectSidebarState,
  selectSideMenu,
  setSidebarState,
} from '../../slice';
import { selectStatus, selectUser } from '../../../user';

import { MenuItem, sidebarItemMapper } from './helpers';
import { useStyles } from './Sidebar.styles';
import {
  SIDEBAR_EXPAND_BUTTON_CLOSED_WIDTH,
  SIDEBAR_EXPAND_BUTTON_OPENED_WIDTH,
  TIMEOUT_FOR_FADE,
} from './Sidebar.constants';

interface SidebarProps {
  logo: ReactNode;
  theme?: 'vk' | 'widget';
  onAction?: (pageTitle: string) => void;
}

export const Sidebar = ({ logo, theme, onAction = () => {} }: SidebarProps) => {
  const dispatch = useDispatch();
  const { pathname } = useLocation();
  const user = useSelector(selectUser);
  const urlWithPrefix = useUrlWithPrefix();

  const localStorageService = useInject<LocalStorage>(LocalStorage);

  const sidebarOpened = useSelector(selectSidebarState);
  const userPermissionsStatus = useSelector(selectStatus);
  const sideMenu = useSelector(selectSideMenu) as MenuItem[];

  const [additionalOptions] = useState<
    Record<UserRoleEnum, NavigationExternalItem[]> | undefined
  >(undefined);

  const isSkeletonLoading = userPermissionsStatus === 'loading';

  const sidebarIconWidth = sidebarOpened
    ? SIDEBAR_EXPAND_BUTTON_OPENED_WIDTH
    : SIDEBAR_EXPAND_BUTTON_CLOSED_WIDTH;

  const { classes } = useStyles({
    sidebarIconWidth,
    theme,
  });

  const handleSidebarOpen = useCallback(
    (value: boolean) => {
      dispatch(setSidebarState(value));
      localStorageService.setItem('sidebarState', value.toString());
    },
    [dispatch, localStorageService],
  );

  const handleIsActive = (path: string, href: string): boolean => {
    if (href === '/') {
      return path === href;
    }
    if (href === '/settings') {
      return (
        path === href ||
        path.includes('/event-types') ||
        path.includes('/settings')
      );
    }
    if (path.startsWith('/personal') && href !== '/news') {
      const currentUserId = pathname.split('/personal/')[1] || '';
      return user?.id !== currentUserId;
    }
    return (
      path === href ||
      path === `/employee${href}` ||
      path.startsWith(`${href}/`) ||
      path.startsWith(`/employee${href}/`)
    );
  };

  const handleSidebarToggle = () => handleSidebarOpen(!sidebarOpened);

  const renderSidebarItem = (menuItem: MenuItem) => {
    const mapper =
      sidebarItemMapper[menuItem.type as keyof typeof sidebarItemMapper];

    if (!mapper) {
      return null;
    }

    return mapper({
      item: menuItem,
      sidebarOpened,
      isSkeletonLoading,
      isActive: handleIsActive,
      pathname,
      additionalOptions,
      onMenuItemClick: onAction,
    });
  };

  return (
    <Box
      className={classNames(
        classes.sidebar,
        'aqa_sidebar',
        sidebarOpened && classes.sidebarOpened,
      )}
    >
      <Box className={classNames(classes.sidebarLogo, 'aqa_sidebarLogo')}>
        <Link {...urlWithPrefix('/')}>{logo}</Link>
      </Box>

      <Box className={classes.overlayTop} />

      <Box className={classNames(classes.sidebarMenu, 'aqa_sidebarMenu')}>
        {sideMenu && sideMenu.map(renderSidebarItem)}
      </Box>

      <Box className={classes.overlayBottom} />

      <Box
        className={classNames(
          classes.sideBarExpandButton,
          'aqa_sidebarExpandButton',
        )}
        onClick={handleSidebarToggle}
      >
        <Tooltip
          placement="right"
          title="Развернуть меню"
          disabled={sidebarOpened}
        >
          <Box
            minWidth="24px"
            display="flex"
            alignItems="center"
            justifyContent="center"
            className="aqa_sidebarExpandIcon"
          >
            <Icon
              name={sidebarOpened ? 'chevron_left_2' : 'chevron_right_2'}
              size="20"
              color="text.light.tertirary"
            />
          </Box>
        </Tooltip>

        <Fade in={sidebarOpened} timeout={TIMEOUT_FOR_FADE}>
          <Box
            width="100%"
            mr="auto"
            height="20px"
            className="aqa_sidebarExpandText"
          >
            <Typography variant="body3" color="text.light.tertirary" noWrap>
              Скрыть меню
            </Typography>
          </Box>
        </Fade>
      </Box>
    </Box>
  );
};
